import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiMessageCircle, FiShield } from 'react-icons/fi';
import { translateLegacyContent, translateLegacyValue } from '../i18n/legacyContent';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from './AuthContext';
import { getApiBase } from '../utils/runtime';
import { BOOKING_AGENT_COPY } from '../booking/config';
import { buildBookingSummary, calculateBookingPricing, determineCaregiverTier, matchProviders } from '../booking/logic';
import {
  clearBookingAgentState,
  loadBookingAgentState,
  saveBookingAgentState,
  savePaymentDraft
} from '../booking/storage';
import {
  buildConversationalSummary,
  buildRecommendationCopy,
  createMessage,
  getInitialConversation,
  getPrompt,
  getRouteAcknowledgement,
  inferRouteFromNeed,
  isValidEmail,
  isValidPhone,
  mapMobilityToAssessment,
  resolveScheduleChoice
} from '../booking/conversationFlow';
import BookingChatComposer from './booking-chat/BookingChatComposer';
import BookingChatMessage from './booking-chat/BookingChatMessage';
import './BookingAgentPage.css';

const API_BASE = getApiBase();
const PUBLIC_MEDIA_BASE = process.env.REACT_APP_PUBLIC_MEDIA_BASE || process.env.REACT_APP_PUBLIC_S3_BASE_URL || '';
const PROVIDER_TERM_TRANSLATIONS = {
  'thérapeute palliative': 'Palliative therapist',
  'therapeute palliative': 'Palliative therapist',
  'accompagnement fin de vie': 'End-of-life support',
  'auxiliaire familiale / préposée à domicile': 'Family care aide / home support worker',
  'auxiliaire familiale / preposee a domicile': 'Family care aide / home support worker',
  'infirmier(e) / infirmier(e) auxiliaire': 'Nurse / auxiliary nurse',
  'infirmiere / infirmiere auxiliaire': 'Nurse / auxiliary nurse',
  'infirmier / infirmier auxiliaire': 'Nurse / auxiliary nurse',
  'test sanguin': 'Blood testing',
  'prise de sang': 'Blood draw',
  'analyses de sang': 'Blood tests',
  "lavage d'oreille": 'Ear wash',
  'lavage d’oreille': 'Ear wash',
  'injection de médicaments': 'Medication injection',
  'injection de medicaments': 'Medication injection',
  'psychothérapeute / travailleuse sociale': 'Psychotherapist / social worker',
  'psychotherapeute / travailleuse sociale': 'Psychotherapist / social worker',
  'psychothérapie familiale': 'Family psychotherapy',
  'psychotherapie familiale': 'Family psychotherapy',
  'psychothérapie individuelle': 'Individual psychotherapy',
  'psychotherapie individuelle': 'Individual psychotherapy',
  'psychothérapie pour couple': 'Couples psychotherapy',
  'psychotherapie pour couple': 'Couples psychotherapy',
  'suivi psychosocial': 'Psychosocial follow-up',
  'nutritionniste': 'Nutritionist',
  'évaluation initiale de nutrition': 'Initial nutrition assessment',
  'evaluation initiale de nutrition': 'Initial nutrition assessment',
  'évaluation nutritionnelle': 'Nutritional assessment',
  'evaluation nutritionnelle': 'Nutritional assessment',
  'suivi nutritionnel': 'Nutrition follow-up',
  'suivi nutritionnel - 30min': 'Nutrition follow-up - 30 min',
  'consultation nutrition': 'Nutrition consultation',
  'soutien à domicile': 'Home support',
  'soutien a domicile': 'Home support',
  'préparation des repas': 'Meal preparation',
  'preparation des repas': 'Meal preparation',
  'soins personnels': 'Personal care',
  'préposé aux bénéficiaires': 'Care attendant',
  'prepose aux beneficiaires': 'Care attendant',
  'français': 'French',
  'anglais': 'English',
  'espagnol': 'Spanish',
  'créole': 'Creole',
  'creole': 'Creole',
  'italien': 'Italian',
  'roumain': 'Romanian',
  'arabe': 'Arabic',
  'russe': 'Russian'
};

const initialCaregiverAssessment = {
  autonomy: 'autonomous',
  mobility: 'independent',
  liftingRequired: false,
  transfersRequired: false,
  repositioningRequired: false,
  heavyHygieneSupport: false
};

function normalizeProviderTerm(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function inferScheduleDate(freeText) {
  const text = normalizeText(freeText);
  if (/aujourd|today|asap/.test(text)) return new Date().toISOString().slice(0, 10);
  if (/demain|tomorrow/.test(text)) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
  }
  return '';
}

function inferPostalCode(freeText) {
  const match = String(freeText || '').match(/\b([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)\b/);
  return match ? match[1].toUpperCase() : '';
}

function inferLanguagePreference(freeText, fallbackLanguage) {
  const text = normalizeText(freeText);
  if (text.includes('francais') || text.includes('français')) return 'French';
  if (text.includes('anglais') || text.includes('english')) return 'English';
  return fallbackLanguage === 'fr' ? 'French' : 'English';
}

function inferCaregiverServices(freeText) {
  const text = normalizeText(freeText);
  const options = [];
  if (/meal|repas|cuisine/.test(text)) options.push('Meal prep');
  if (/medic|pill|pharma/.test(text)) options.push('Medication reminders');
  if (/mobil|walker|transfer|marche/.test(text)) options.push('Mobility support');
  if (/hygiene|bath|toilet|bain/.test(text)) options.push('Hygiene support');
  if (!options.length) options.push('Companionship');
  return options;
}

function inferNurseServiceType(freeText) {
  const text = normalizeText(freeText);
  if (/blood|prise de sang|prelevement|analyse/.test(text)) return 'blood_draw';
  if (/inject/.test(text)) return 'injection';
  if (/plaie|wound|pansement|dressing/.test(text)) return 'wound_care';
  if (/oreille|ear/.test(text)) return 'ear_wash';
  return 'other_nursing_support';
}

function inferOtherServiceCategory(freeText) {
  const text = normalizeText(freeText);
  if (/nutrition|diet/.test(text)) return 'nutrition';
  if (/mental|psych|therap|social worker|counsel/.test(text)) return 'therapy';
  if (/foot|pied|podiat|chirop/.test(text)) return 'foot_care';
  if (/physio|rehab|mobilit/.test(text)) return 'physiotherapy';
  return 'other';
}

function buildUserIntakePrefill(user) {
  if (!user) return {};
  const fullName = `${user.given_name || user.first_name || ''} ${user.family_name || user.last_name || ''}`.trim();
  return {
    beneficiaryName: fullName || user.name || '',
    contactName: fullName || user.name || '',
    email: user.email || '',
    phone: user.phone_number || user.phone || '',
    postalCode: user.postal_code || user.address || '',
    preferredLanguage: user.locale || ''
  };
}

function buildConciergeIntakePrefill({ freeText, selectedRoute, currentIntake, language }) {
  const route = selectedRoute || inferRouteFromNeed(freeText, '');
  const patch = {};
  const text = String(freeText || '').trim();
  if (!text) return patch;

  if (!currentIntake.postalCode) patch.postalCode = inferPostalCode(text);
  if (!currentIntake.preferredLanguage) patch.preferredLanguage = inferLanguagePreference(text, language);
  if (!currentIntake.scheduleDate) patch.scheduleDate = inferScheduleDate(text);

  if (route === 'caregiver') {
    if (!currentIntake.requestedServices?.length) patch.requestedServices = inferCaregiverServices(text);
    if (!currentIntake.healthSafetyNotes) patch.healthSafetyNotes = text;
  }

  if (route === 'nurse') {
    if (!currentIntake.nurseServiceType) patch.nurseServiceType = inferNurseServiceType(text);
    if (!currentIntake.serviceReason) patch.serviceReason = text;
    if (!currentIntake.symptomsGoals) patch.symptomsGoals = text;
  }

  if (route === 'other') {
    if (!currentIntake.otherServiceCategory) patch.otherServiceCategory = inferOtherServiceCategory(text);
    if (!currentIntake.consultationReason) patch.consultationReason = text;
    if (!currentIntake.symptomsGoals) patch.symptomsGoals = text;
  }

  return patch;
}

function splitFullName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || parts[0] || ''
  };
}

function formatCurrency(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function formatSlot(slot) {
  const startShort = String(slot.start_time || '').slice(0, 5);
  const endShort = String(slot.end_time || '').slice(0, 5);
  return `${slot.date} ${startShort} - ${endShort}`;
}

function computeDurationMinutes(startTime, endTime) {
  const [startHour, startMinute] = String(startTime || '00:00').split(':').map(Number);
  const [endHour, endMinute] = String(endTime || '00:00').split(':').map(Number);
  return Math.max(((endHour * 60) + endMinute) - ((startHour * 60) + startMinute), 30);
}

function normalizeNamedValues(values) {
  return (values || [])
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      return entry?.name || entry?.language || '';
    })
    .filter(Boolean);
}

function resolveProviderImageUrl(photoUrl) {
  const value = String(photoUrl || '').trim();
  if (!value || value.startsWith('s3://')) return null;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  if (value.startsWith('/')) return value;
  if (PUBLIC_MEDIA_BASE) {
    return `${PUBLIC_MEDIA_BASE.replace(/\/+$/, '')}/${value.replace(/^\/+/, '')}`;
  }
  return value;
}

function getInitials(provider) {
  const first = String(provider?.first_name || '').trim();
  const last = String(provider?.last_name || '').trim();
  return `${first[0] || ''}${last[0] || first[1] || ''}`.toUpperCase() || 'KM';
}

async function fetchLegacyProviders() {
  const response = await axios.get(`${API_BASE}/prestataires`);
  const providers = response.data || [];

  const enriched = await Promise.all(
    providers.map(async (provider) => {
      try {
        const servicesResponse = await axios.get(`${API_BASE}/professionals/${provider.user_id}/services`);
        const services = (servicesResponse.data || []).map((service) => ({
          ...service,
          duration_minutes: service.duration_minutes || 60,
          price_cents: service.price_cents || 0
        }));

        return {
          provider_id: provider.user_id,
          first_name: provider.first_name,
          last_name: provider.last_name,
          email_verified: true,
          zone_id: null,
          zone_name: provider.zone_name || null,
          photo_url: provider.photo_url || null,
          license_number: null,
          speciality_label: provider.specialities?.[0] || null,
          clinic_name: null,
          years_experience: null,
          availability_count: 1,
          is_active: true,
          services,
          specialities: (provider.specialities || []).map((name, index) => ({
            speciality_id: `${provider.user_id}-speciality-${index}`,
            name,
            license_number: null
          })),
          languages: (provider.languages || []).map((entry) => ({
            language: entry,
            level: null
          }))
        };
      } catch {
        return null;
      }
    })
  );

  return enriched.filter(Boolean);
}

function createPromptMessage(prompt) {
  return createMessage({
    kind: prompt.input === 'text' ? 'text' : prompt.input,
    text: prompt.text,
    promptKey: prompt.key,
    options: prompt.options,
    submitLabel: prompt.submitLabel
  });
}

function shouldDisplayPromptMessage(prompt) {
  return Boolean(prompt && prompt.key !== 'serviceNeed');
}

function createInitialState(language) {
  const initialPrompt = getPrompt('serviceNeed', language);
  return {
    messages: [...getInitialConversation(language)],
    pendingPrompt: initialPrompt,
    composerText: '',
    selectedRoute: '',
    freeText: '',
    intake: {},
    caregiverAssessment: { ...initialCaregiverAssessment },
    draftSelections: {},
    selectedProviderId: '',
    selectedServiceId: '',
    selectedSlotId: '',
    needsProviderRefresh: false,
    checkoutShown: false
  };
}

function BookingAgentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user, loginPatient, signupPatient } = useAuth();
  const copy = BOOKING_AGENT_COPY[language] || BOOKING_AGENT_COPY.en;
  const bottomRef = useRef(null);
  const hydratedRef = useRef(false);

  const [state, setState] = useState(() => createInitialState(language));
  const [providers, setProviders] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingProviderProfile, setLoadingProviderProfile] = useState(false);
  const [selectedProviderProfile, setSelectedProviderProfile] = useState(null);
  const [error, setError] = useState('');

  const route = state.selectedRoute || inferRouteFromNeed(state.freeText, '');
  const caregiverTier = useMemo(
    () => (route === 'caregiver' ? determineCaregiverTier(state.caregiverAssessment) : null),
    [route, state.caregiverAssessment]
  );

  const routeContext = useMemo(() => ({
    providerRoute: route || 'other',
    caregiverTier,
    intake: state.intake
  }), [caregiverTier, route, state.intake]);

  const matchedProviders = useMemo(() => {
    if (!providers.length) return [];
    return matchProviders(providers, routeContext);
  }, [providers, routeContext]);

  const selectedProvider = useMemo(
    () => matchedProviders.find((provider) => provider.provider_id === state.selectedProviderId) || null,
    [matchedProviders, state.selectedProviderId]
  );

  const selectedService = useMemo(
    () => selectedProvider?.matchedServices?.find((service) => service.service_id === state.selectedServiceId) || null,
    [selectedProvider, state.selectedServiceId]
  );

  const selectedSlot = useMemo(() => {
    const slot = slots.find((entry) => entry.id === state.selectedSlotId);
    if (!slot) return null;
    return {
      ...slot,
      label: formatSlot(slot),
      durationMinutes: computeDurationMinutes(slot.start_time, slot.end_time),
      start: `${slot.date} ${slot.start_time}`,
      end: `${slot.date} ${slot.end_time}`
    };
  }, [slots, state.selectedSlotId]);

  const pricing = useMemo(() => {
    if (!selectedService || !selectedSlot) return null;
    return calculateBookingPricing({
      routeContext,
      selectedService,
      slot: selectedSlot
    });
  }, [routeContext, selectedService, selectedSlot]);

  const bookingSummary = useMemo(() => {
    if (!selectedProvider || !selectedService || !selectedSlot || !pricing) return null;
    return buildBookingSummary({
      locale: language,
      routeContext,
      provider: selectedProvider,
      service: selectedService,
      slot: selectedSlot,
      pricing
    });
  }, [language, pricing, routeContext, selectedProvider, selectedService, selectedSlot]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [state.messages]);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const saved = loadBookingAgentState();
    const conciergePrefill = location.state?.conciergePrefill;
    const userPrefill = buildUserIntakePrefill(user);

    const isLegacyState = saved && (!Array.isArray(saved.messages) || !saved.pendingPrompt?.key);

    if (!saved || isLegacyState) {
      const baseState = createInitialState(language);
      const mergedIntake = {
        ...(saved?.intake || {}),
        ...userPrefill,
        ...(conciergePrefill?.intake || {})
      };

      const freeText = conciergePrefill?.freeText || saved?.freeText || '';
      const selectedRoute = conciergePrefill?.selectedRoute || saved?.selectedRoute || '';
      const intake = {
        ...mergedIntake,
        ...buildConciergeIntakePrefill({
          freeText,
          selectedRoute,
          currentIntake: mergedIntake,
          language
        })
      };

      setState({
        ...baseState,
        intake,
        freeText,
        selectedRoute,
        caregiverAssessment: saved?.caregiverAssessment || baseState.caregiverAssessment
      });
      return;
    }

    const hydratedPrompt = saved.pendingPrompt?.key
      ? getPrompt(saved.pendingPrompt.key, language, {
        route: saved.selectedRoute || inferRouteFromNeed(saved.freeText, ''),
        patient: saved.intake?.patientRelationship
      })
      : null;
    const messages = hydratedPrompt && shouldDisplayPromptMessage(hydratedPrompt) && !saved.messages.some((message) => message.promptKey === hydratedPrompt.key)
      ? [...saved.messages, createPromptMessage(hydratedPrompt)]
      : saved.messages;

    setState((current) => ({
      ...current,
      ...saved,
      messages,
      intake: {
        ...saved.intake,
        ...userPrefill
      },
      pendingPrompt: hydratedPrompt,
      composerText: ''
    }));
  }, [language, location.state, user]);

  useEffect(() => {
    if (!user) return;
    const userPrefill = buildUserIntakePrefill(user);
    setState((current) => ({
      ...current,
      intake: {
        ...userPrefill,
        ...current.intake
      }
    }));
  }, [user]);

  useEffect(() => {
    saveBookingAgentState(state);
  }, [state]);

  useEffect(() => {
    let active = true;
    setLoadingProviders(true);

    (async () => {
      try {
        const nextProviders = await fetchLegacyProviders();
        if (active) {
          setProviders(nextProviders);
          if (!nextProviders.length) setError(language === 'fr' ? 'Impossible de charger les prestataires.' : 'Unable to load providers.');
        }
      } catch {
        if (active) setError(language === 'fr' ? 'Impossible de charger les prestataires.' : 'Unable to load providers.');
      } finally {
        if (active) setLoadingProviders(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [language]);

  useEffect(() => {
    if (loadingProviders || !state.needsProviderRefresh) return;

    const nextProviders = matchProviders(providers, routeContext).slice(0, 3);
    const recommendation = buildRecommendationCopy({
      language,
      route,
      intake: state.intake
    });

    setState((current) => ({
      ...current,
      needsProviderRefresh: false,
      messages: [
        ...current.messages,
        createMessage({ text: recommendation }),
        createMessage({
          kind: 'providers',
          text: language === 'fr'
            ? 'Voici les options qui correspondent le mieux à ce que vous m’avez partagé.'
            : 'Here are the options that best match what you shared.',
          providers: nextProviders,
          route,
          promptKey: 'providerSelect'
        }),
        ...(!nextProviders.length ? [createMessage({
          text: copy.providersEmpty
        })] : [])
      ],
      pendingPrompt: nextProviders.length ? { key: 'providerSelect', input: 'providers' } : getPrompt('additionalNotes', language, { route })
    }));
  }, [copy.providersEmpty, language, loadingProviders, providers, route, routeContext, state.intake, state.needsProviderRefresh]);

  function localizeProviderValue(value, currentLanguage = language) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (currentLanguage !== 'en') return raw;
    const translated = translateLegacyValue(raw, currentLanguage) || raw;
    const normalized = normalizeProviderTerm(translated);
    return PROVIDER_TERM_TRANSLATIONS[normalized] || translated;
  }

  function formatProviderList(values) {
    return normalizeNamedValues(values).map((value) => localizeProviderValue(value, language));
  }

  function renderProviderImage(provider, className = 'booking-agent__provider-image') {
    const src = resolveProviderImageUrl(provider.photo_url);
    if (src) {
      return <img alt={`${provider.first_name} ${provider.last_name || ''}`.trim()} className={className} src={src} />;
    }

    return (
      <div className={`${className} booking-agent__provider-image--fallback`} aria-hidden="true">
        <span>{getInitials(provider)}</span>
      </div>
    );
  }

  function promptContext(currentState = state) {
    return {
      route: currentState.selectedRoute || inferRouteFromNeed(currentState.freeText, ''),
      patient: currentState.intake.patientRelationship
    };
  }

  function appendPrompt(currentState, promptKey) {
    const prompt = getPrompt(promptKey, language, promptContext(currentState));
    if (!prompt) return currentState;
    return {
      ...currentState,
      pendingPrompt: prompt,
      messages: shouldDisplayPromptMessage(prompt)
        ? [...currentState.messages, createPromptMessage(prompt)]
        : currentState.messages,
      composerText: ''
    };
  }

  function recordUserMessage(text) {
    setState((current) => ({
      ...current,
      messages: [...current.messages, createMessage({ role: 'user', text })],
      composerText: ''
    }));
  }

  function clearDownstreamSelection(current) {
    return {
      ...current,
      selectedProviderId: '',
      selectedServiceId: '',
      selectedSlotId: '',
      checkoutShown: false
    };
  }

  function nextPostSlotPrompt(currentState) {
    if (currentState.intake.patientRelationship === 'loved_one' && !currentState.intake.beneficiaryName) return 'beneficiaryName';
    if (!currentState.intake.contactName) return 'contactName';
    if (!currentState.intake.email) return 'email';
    if (!currentState.intake.phone) return 'phone';
    return null;
  }

  function showSummary(currentState) {
    const provider = matchedProviders.find((entry) => entry.provider_id === currentState.selectedProviderId) || selectedProvider;
    const service = provider?.matchedServices?.find((entry) => entry.service_id === currentState.selectedServiceId) || selectedService;
    const slot = slots.find((entry) => entry.id === currentState.selectedSlotId);
    const slotSummary = slot ? { ...slot, label: formatSlot(slot) } : selectedSlot;

    const summaryItems = buildConversationalSummary({
      language,
      intake: currentState.intake,
      selectedProvider: provider,
      selectedService: service,
      selectedSlot: slotSummary
    });

    return {
      ...currentState,
      pendingPrompt: { key: 'summaryActions', input: 'summary' },
      messages: [
        ...currentState.messages,
        createMessage({
          kind: 'summary',
          text: language === 'fr'
            ? 'Voici le résumé conversationnel de votre demande.'
            : 'Here is the conversational summary of your request.',
          items: summaryItems,
          actions: [
            { value: 'edit_service', label: language === 'fr' ? 'Modifier le service' : 'Edit service' },
            { value: 'edit_mobility', label: language === 'fr' ? 'Modifier la mobilité' : 'Edit mobility' },
            { value: 'add_details', label: language === 'fr' ? 'Ajouter des détails' : 'Add details' },
            { value: 'continue', label: language === 'fr' ? 'Continuer' : 'Continue', primary: true }
          ]
        })
      ]
    };
  }

  function finalizeAfterSlot() {
    setState((current) => {
      const promptKey = nextPostSlotPrompt(current);
      if (!promptKey) {
        return showSummary(current);
      }

      const withLeadIn = {
        ...current,
        messages: [
          ...current.messages,
          createMessage({
            text: language === 'fr'
              ? 'Parfait. J’ai juste besoin de quelques coordonnées pour finaliser la réservation.'
              : 'Perfect. I just need a few contact details to finalize the booking.'
          })
        ]
      };

      return appendPrompt(withLeadIn, promptKey);
    });
  }

  function handleServiceNeedAnswer(text) {
    const nextRoute = inferRouteFromNeed(text, state.selectedRoute);
    const prefill = buildConciergeIntakePrefill({
      freeText: text,
      selectedRoute: nextRoute,
      currentIntake: state.intake,
      language
    });

    setState((current) => {
      const nextState = clearDownstreamSelection({
        ...current,
        freeText: text,
        selectedRoute: nextRoute,
        intake: {
          ...current.intake,
          ...prefill
        },
        caregiverAssessment: current.intake.mobilityStatus
          ? current.caregiverAssessment
          : mapMobilityToAssessment(current.intake.mobilityStatus)
      });

      return appendPrompt({
        ...nextState,
        messages: [
          ...nextState.messages,
          createMessage({ role: 'user', text }),
          createMessage({ text: getRouteAcknowledgement(nextRoute, text, language) })
        ]
      }, 'patient');
    });
  }

  function handleTextSubmit(event) {
    event.preventDefault();
    const value = String(state.composerText || '').trim();
    if (!value || !state.pendingPrompt) return;
    setError('');

    if (state.pendingPrompt.key === 'serviceNeed') {
      handleServiceNeedAnswer(value);
      return;
    }

    if (state.pendingPrompt.key === 'routeDetails') {
      setState((current) => {
        const nextIntake = { ...current.intake };
        if (route === 'caregiver') {
          nextIntake.healthSafetyNotes = value;
          nextIntake.emergencySupportDetails = value;
        } else if (route === 'nurse') {
          nextIntake.serviceReason = value;
          nextIntake.symptomsGoals = value;
        } else {
          nextIntake.consultationReason = value;
          nextIntake.symptomsGoals = value;
          nextIntake.searchDuration = language === 'fr' ? 'Je commence maintenant' : 'Starting now';
        }

        return appendPrompt({
          ...clearDownstreamSelection(current),
          intake: nextIntake,
          messages: [...current.messages, createMessage({ role: 'user', text: value })]
        }, 'location');
      });
      return;
    }

    if (state.pendingPrompt.key === 'location') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          postalCode: value
        },
        messages: [...current.messages, createMessage({ role: 'user', text: value })]
      }, 'schedule'));
      return;
    }

    if (state.pendingPrompt.key === 'additionalNotes') {
      setState((current) => ({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          notes: value
        },
        messages: [...current.messages, createMessage({ role: 'user', text: value })],
        pendingPrompt: null,
        needsProviderRefresh: true
      }));
      return;
    }

    if (state.pendingPrompt.key === 'beneficiaryName') {
      setState((current) => appendPrompt({
        ...current,
        intake: {
          ...current.intake,
          beneficiaryName: value
        },
        messages: [...current.messages, createMessage({ role: 'user', text: value })]
      }, 'contactName'));
      return;
    }

    if (state.pendingPrompt.key === 'contactName') {
      setState((current) => {
        const next = {
          ...current,
          intake: {
            ...current.intake,
            contactName: value,
            beneficiaryName: current.intake.beneficiaryName || value
          },
          messages: [...current.messages, createMessage({ role: 'user', text: value })]
        };
        return appendPrompt(next, 'email');
      });
      return;
    }

    if (state.pendingPrompt.key === 'email') {
      if (!isValidEmail(value)) {
        setState((current) => ({
          ...current,
          messages: [...current.messages, createMessage({
            text: language === 'fr'
              ? 'Cette adresse courriel semble incomplète. Pouvez-vous la vérifier ?'
              : 'That email looks incomplete. Could you check it and send it again?'
          })],
          composerText: ''
        }));
        return;
      }

      setState((current) => appendPrompt({
        ...current,
        intake: {
          ...current.intake,
          email: value
        },
        messages: [...current.messages, createMessage({ role: 'user', text: value })]
      }, 'phone'));
      return;
    }

    if (state.pendingPrompt.key === 'phone') {
      if (!isValidPhone(value)) {
        setState((current) => ({
          ...current,
          messages: [...current.messages, createMessage({
            text: language === 'fr'
              ? 'Ce numéro semble trop court. Pouvez-vous le renvoyer avec l’indicatif ?'
              : 'That phone number looks too short. Could you send it again with the area code?'
          })],
          composerText: ''
        }));
        return;
      }

      setState((current) => showSummary({
        ...current,
        intake: {
          ...current.intake,
          phone: value
        },
        messages: [...current.messages, createMessage({ role: 'user', text: value })]
      }));
    }
  }

  function handleQuickReply(reply) {
    setState((current) => ({ ...current, composerText: reply }));
  }

  function handleSkipPrompt() {
    if (state.pendingPrompt?.key !== 'additionalNotes') return;
    setState((current) => ({
      ...clearDownstreamSelection(current),
      pendingPrompt: null,
      messages: [...current.messages, createMessage({
        role: 'user',
        text: language === 'fr' ? 'Pas de détail supplémentaire.' : 'No extra details.'
      })],
      needsProviderRefresh: true
    }));
  }

  function handleOptionSelect(promptKey, value) {
    setError('');

    if (promptKey === 'patient') {
      setState((current) => {
        const next = {
          ...current,
          intake: {
            ...current.intake,
            patientRelationship: value
          },
          messages: [
            ...current.messages,
            createMessage({
              role: 'user',
              text: value === 'self'
                ? (language === 'fr' ? 'C’est pour moi.' : 'This is for me.')
                : (language === 'fr' ? 'C’est pour un proche.' : 'This is for a loved one.')
            })
          ]
        };
        return appendPrompt(next, 'mobility');
      });
      return;
    }

    if (promptKey === 'mobility') {
      setState((current) => {
        const next = clearDownstreamSelection({
          ...current,
          caregiverAssessment: mapMobilityToAssessment(value),
          intake: {
            ...current.intake,
            mobilityStatus: value
          },
          messages: [...current.messages, createMessage({
            role: 'user',
            text: getPrompt('mobility', language).options.find((option) => option.value === value)?.label || value
          })]
        });

        if (route === 'caregiver') return appendPrompt(next, 'caregiverServices');
        if (route === 'nurse') return appendPrompt(next, 'nurseServiceType');
        return appendPrompt(next, 'otherServiceCategory');
      });
      return;
    }

    if (promptKey === 'nurseServiceType') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          nurseServiceType: value
        },
        messages: [...current.messages, createMessage({
          role: 'user',
          text: getPrompt('nurseServiceType', language).options.find((option) => option.value === value)?.label || value
        })]
      }, 'routeDetails'));
      return;
    }

    if (promptKey === 'otherServiceCategory') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          otherServiceCategory: value
        },
        messages: [...current.messages, createMessage({
          role: 'user',
          text: getPrompt('otherServiceCategory', language).options.find((option) => option.value === value)?.label || value
        })]
      }, 'routeDetails'));
      return;
    }

    if (promptKey === 'schedule') {
      const schedule = resolveScheduleChoice(value, language);
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          scheduleDate: schedule.scheduleDate,
          schedulePreferenceLabel: schedule.label,
          notes: current.intake.notes || schedule.notes
        },
        messages: [...current.messages, createMessage({ role: 'user', text: schedule.label })]
      }, 'preferredLanguage'));
      return;
    }

    if (promptKey === 'preferredLanguage') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        intake: {
          ...current.intake,
          preferredLanguage: value
        },
        messages: [...current.messages, createMessage({
          role: 'user',
          text: getPrompt('preferredLanguage', language).options.find((option) => option.value === value)?.label || value
        })]
      }, 'additionalNotes'));
    }
  }

  function handleToggleChip(promptKey, value) {
    setState((current) => {
      const selectedValues = current.draftSelections[promptKey] || [];
      const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((entry) => entry !== value)
        : [...selectedValues, value];

      return {
        ...current,
        draftSelections: {
          ...current.draftSelections,
          [promptKey]: nextValues
        }
      };
    });
  }

  function handleCommitMultiSelect(promptKey) {
    if (promptKey !== 'caregiverServices') return;
    const values = state.draftSelections[promptKey] || [];
    if (!values.length) return;

    setState((current) => appendPrompt({
      ...clearDownstreamSelection(current),
      intake: {
        ...current.intake,
        requestedServices: values
      },
      messages: [...current.messages, createMessage({
        role: 'user',
        text: values.join(', ')
      })]
    }, 'routeDetails'));
  }

  async function handleProviderPreview(provider) {
    setLoadingProviderProfile(true);
    setSelectedProviderProfile({
      user_id: provider.provider_id,
      first_name: provider.first_name,
      last_name: provider.last_name,
      photo_url: provider.photo_url,
      specialities: (provider.specialities || []).map((item) => item.name).filter(Boolean),
      services: (provider.services || []).map((item) => item.name).filter(Boolean),
      languages: (provider.languages || []).map((item) => item.language).filter(Boolean),
      cultures: [],
      modes: []
    });

    try {
      const response = await axios.get(`${API_BASE}/prestataires/${provider.provider_id}`);
      setSelectedProviderProfile(response.data || null);
    } catch {
      setError(language === 'fr' ? 'Impossible de charger le profil du prestataire.' : 'Unable to load the provider profile.');
    } finally {
      setLoadingProviderProfile(false);
    }
  }

  function closeProviderProfile() {
    setSelectedProviderProfile(null);
    setLoadingProviderProfile(false);
  }

  function handleProviderSelect(provider, service) {
    setLoadingSlots(true);
    setError('');
    setSlots([]);

    setState((current) => ({
      ...current,
      selectedProviderId: provider.provider_id,
      selectedServiceId: service.service_id,
      selectedSlotId: '',
      pendingPrompt: { key: 'slotSelect', input: 'slots' },
      messages: [
        ...current.messages,
        createMessage({
          role: 'user',
          text: `${provider.first_name} ${provider.last_name} - ${localizeProviderValue(service.name, language)}`
        }),
        createMessage({
          text: language === 'fr'
            ? `Très bon choix. Je cherche maintenant les meilleurs créneaux pour ${provider.first_name}.`
            : `Great choice. I am looking up the best time slots for ${provider.first_name} now.`
        })
      ]
    }));

    axios
      .get(`${API_BASE}/professionals/${provider.provider_id}/weekly-availabilities`, {
        params: { service_id: service.service_id }
      })
      .then(async (response) => {
        let allSlots = response.data || [];
        if (!allSlots.length) {
          const fallbackResponse = await axios.get(`${API_BASE}/professionals/${provider.provider_id}/weekly-availabilities`);
          allSlots = fallbackResponse.data || [];
        }

        const preferredDateSlots = allSlots.filter(
          (slot) => !state.intake.scheduleDate || slot.date === state.intake.scheduleDate
        );
        const nextSlots = (preferredDateSlots.length ? preferredDateSlots : allSlots).slice(0, 8);
        setSlots(nextSlots);

        setState((current) => ({
          ...current,
          pendingPrompt: nextSlots.length ? { key: 'slotSelect', input: 'slots' } : current.pendingPrompt,
          messages: [
            ...current.messages,
            createMessage({
              kind: 'slots',
              text: language === 'fr'
                ? 'Voici les créneaux les plus pertinents. Choisissez celui qui vous convient.'
                : 'Here are the most relevant openings. Pick the one that works best.',
              slots: nextSlots.map((slot) => ({
                ...slot,
                label: formatSlot(slot)
              })),
              promptKey: 'slotSelect'
            }),
            ...(!nextSlots.length ? [createMessage({
              text: language === 'fr'
                ? 'Je n’ai pas trouvé de disponibilité exacte pour cette préférence, mais je peux ajuster les détails.'
                : 'I did not find an exact opening for that preference yet, but I can adjust the details.'
            })] : [])
          ]
        }));
      })
      .catch(() => {
        setError(language === 'fr' ? 'Impossible de charger les disponibilités.' : 'Unable to load provider availability.');
      })
      .finally(() => setLoadingSlots(false));
  }

  function handleSlotSelect(slot) {
    setState((current) => ({
      ...current,
      selectedSlotId: slot.id,
      messages: [
        ...current.messages,
        createMessage({ role: 'user', text: slot.label }),
        createMessage({
          text: language === 'fr'
            ? 'Excellent. Je prépare maintenant votre résumé et les prochaines étapes.'
            : 'Excellent. I am preparing your summary and next steps now.'
        })
      ]
    }));

    window.setTimeout(finalizeAfterSlot, 0);
  }

  function buildPaymentPayload() {
    if (!selectedProvider || !selectedService || !selectedSlot || !pricing || !bookingSummary) {
      return null;
    }

    const contactParts = splitFullName(state.intake.contactName || state.intake.beneficiaryName);
    const appointmentData = {
      appointment_id: crypto.randomUUID(),
      patient_id: user?.user_id || undefined,
      professional_id: selectedProvider.provider_id,
      service_id: selectedService.service_id,
      start_time: selectedSlot.start,
      end_time: selectedSlot.end,
      mode: 'in_person',
      postal_code: state.intake.postalCode || null,
      phone: state.intake.phone || '',
      address: state.intake.postalCode || '',
      notes: JSON.stringify({
        route,
        caregiverTier,
        intake: state.intake
      }),
      first_name: contactParts.firstName,
      last_name: contactParts.lastName,
      email: state.intake.email,
      pricing: {
        pricing_model: pricing.pricing_model,
        base_price_cents: pricing.basePriceCents,
        admin_fee_cents: pricing.adminFeeCents,
        total_cents: pricing.totalCents
      }
    };

    const billingInfo = {
      name: state.intake.contactName || state.intake.beneficiaryName,
      email: state.intake.email,
      phone: state.intake.phone,
      address: state.intake.postalCode
    };

    return {
      appointmentData,
      billingInfo,
      servicePriceCents: pricing.totalCents,
      bookingSummary
    };
  }

  function handleSummaryAction(action) {
    if (action === 'edit_service') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        selectedRoute: '',
        freeText: '',
        intake: {
          ...current.intake,
          serviceReason: '',
          consultationReason: '',
          healthSafetyNotes: '',
          symptomsGoals: '',
          requestedServices: [],
          nurseServiceType: '',
          otherServiceCategory: ''
        },
        messages: [...current.messages, createMessage({
          text: language === 'fr'
            ? 'Bien sûr. Reprenons le service depuis le début.'
            : 'Of course. Let’s update the service from the start.'
        })]
      }, 'serviceNeed'));
      return;
    }

    if (action === 'edit_mobility') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        messages: [...current.messages, createMessage({
          text: language === 'fr'
            ? 'Pas de souci. Reprenons la mobilité.'
            : 'No problem. Let’s update the mobility details.'
        })]
      }, 'mobility'));
      return;
    }

    if (action === 'add_details') {
      setState((current) => appendPrompt({
        ...clearDownstreamSelection(current),
        messages: [...current.messages, createMessage({
          text: language === 'fr'
            ? 'D’accord, ajoutez ce que vous souhaitez et je mettrai les recommandations à jour.'
            : 'Absolutely. Add anything you want and I will refresh the recommendations.'
        })]
      }, 'additionalNotes'));
      return;
    }

    if (action === 'continue') {
      setState((current) => {
        if (current.checkoutShown) return current;
        return {
          ...current,
          checkoutShown: true,
          pendingPrompt: { key: 'checkoutActions', input: 'checkout' },
          messages: [
            ...current.messages,
            createMessage({
              kind: 'checkout',
              text: user
                ? (language === 'fr' ? `Vous êtes connecté en tant que ${user.email || user.name || 'patient'}.` : `You are signed in as ${user.email || user.name || 'patient'}.`)
                : (language === 'fr' ? 'Vous pouvez continuer comme invité ou vous connecter avant le paiement.' : 'You can continue as a guest or sign in before payment.'),
              actions: user
                ? [{ value: 'pay', label: language === 'fr' ? 'Réserver et payer' : 'Book and pay', primary: true }]
                : [
                  { value: 'sign_in', label: copy.signIn },
                  { value: 'create_account', label: language === 'fr' ? 'Créer un compte' : 'Create account' },
                  { value: 'pay', label: language === 'fr' ? 'Continuer comme invité' : 'Continue as guest', primary: true }
                ]
            })
          ]
        };
      });
      return;
    }

    if (action === 'sign_in') {
      const paymentPayload = buildPaymentPayload();
      if (!paymentPayload) {
        setError(language === 'fr' ? 'Des détails de réservation sont manquants.' : 'Missing booking details.');
        return;
      }
      savePaymentDraft(paymentPayload);
      loginPatient({ redirectTo: `${window.location.origin}/paiement` });
      return;
    }

    if (action === 'create_account') {
      const paymentPayload = buildPaymentPayload();
      if (!paymentPayload) {
        setError(language === 'fr' ? 'Des détails de réservation sont manquants.' : 'Missing booking details.');
        return;
      }
      savePaymentDraft(paymentPayload);
      signupPatient({ redirectTo: `${window.location.origin}/paiement` });
      return;
    }

    if (action === 'pay') {
      const paymentPayload = buildPaymentPayload();
      if (!paymentPayload) {
        setError(language === 'fr' ? 'Des détails de réservation sont manquants.' : 'Missing booking details.');
        return;
      }

      savePaymentDraft(paymentPayload);
      clearBookingAgentState();
      navigate('/paiement', {
        state: paymentPayload
      });
    }
  }

  const activeProviderDetail = useMemo(() => {
    if (!selectedProviderProfile) return null;
    return matchedProviders.find((provider) => provider.provider_id === selectedProviderProfile.user_id)
      || matchedProviders.find((provider) => provider.provider_id === selectedProviderProfile.provider_id)
      || null;
  }, [matchedProviders, selectedProviderProfile]);

  return (
    <div className="booking-agent">
      <header className="booking-agent__topbar">
        <h1>{copy.assistantName}</h1>
        <div className="booking-agent__status-row">
          <span className="booking-agent__status-pill"><FiMessageCircle /> {language === 'fr' ? 'En ligne • répond instantanément' : 'Online • replies instantly'}</span>
          <span className="booking-agent__status-pill"><FiShield /> {language === 'fr' ? 'Sécurisé et privé' : 'Secure & private'}</span>
          <span className="booking-agent__status-pill is-context">
            {route ? (copy.routeOptions?.[route] || route) : (language === 'fr' ? 'Découverte' : 'Discovery')}
          </span>
          <span className="booking-agent__status-pill is-context">
            {state.intake.postalCode || (language === 'fr' ? 'Lieu à confirmer' : 'Location pending')}
          </span>
        </div>
      </header>

      <section className="booking-agent__chat-shell">
        {error ? <div className="booking-agent__error">{error}</div> : null}

        <div className="booking-agent__messages">
          {state.messages.map((message) => (
            <BookingChatMessage
              key={message.id}
              language={language}
              message={message}
              isActive={state.pendingPrompt?.key === message.promptKey || ['summary', 'checkout'].includes(message.kind)}
              draftSelections={state.draftSelections}
              onOptionSelect={handleOptionSelect}
              onToggleChip={handleToggleChip}
              onCommitMultiSelect={handleCommitMultiSelect}
              onProviderPreview={handleProviderPreview}
              onProviderSelect={handleProviderSelect}
              onSlotSelect={handleSlotSelect}
              onSummaryAction={handleSummaryAction}
              formatProviderList={formatProviderList}
              renderProviderImage={renderProviderImage}
              localizeProviderValue={localizeProviderValue}
              caregiverTier={caregiverTier}
            />
          ))}

          {loadingProviders && !providers.length ? (
            <article className="booking-chat-message is-assistant">
              <div className="booking-chat-message__bubble">
                <p>{language === 'fr' ? 'Je prépare les correspondances de prestataires en arrière-plan.' : 'I am preparing provider matching in the background.'}</p>
              </div>
            </article>
          ) : null}

          {loadingSlots ? (
            <article className="booking-chat-message is-assistant">
              <div className="booking-chat-message__bubble">
                <p>{language === 'fr' ? 'Recherche des disponibilités...' : 'Looking up availability...'}</p>
              </div>
            </article>
          ) : null}

          <div ref={bottomRef} />
        </div>

        <BookingChatComposer
          language={language}
          pendingPrompt={state.pendingPrompt}
          value={state.composerText}
          onChange={(value) => setState((current) => ({ ...current, composerText: value }))}
          onSubmit={handleTextSubmit}
          onQuickReply={handleQuickReply}
          onSkip={handleSkipPrompt}
        />
      </section>

      {selectedProviderProfile ? (
        <div className="booking-agent__modal-shell" onClick={closeProviderProfile} role="presentation">
          <div className="booking-agent__modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="booking-agent__modal-close" onClick={closeProviderProfile}>
              {language === 'fr' ? 'Fermer' : 'Close'}
            </button>
            <div className="booking-agent__modal-hero">
              {renderProviderImage(
                {
                  first_name: selectedProviderProfile.first_name,
                  last_name: selectedProviderProfile.last_name,
                  photo_url: selectedProviderProfile.photo_url
                },
                'booking-agent__provider-image booking-agent__provider-image--large'
              )}
              <div>
                <p className="booking-chat-message__provider-kicker">
                  {localizeProviderValue(
                    activeProviderDetail?.speciality_label
                      || selectedProviderProfile.specialities?.[0]
                      || '',
                    language
                  )}
                </p>
                <h3>{selectedProviderProfile.first_name} {selectedProviderProfile.last_name}</h3>
                <p>
                  {translateLegacyContent(
                    activeProviderDetail?.matchedServices?.length
                      ? `${language === 'fr' ? 'Services compatibles:' : 'Matched services:'} ${activeProviderDetail.matchedServices.map((service) => service.name).join(', ')}`
                      : (language === 'fr' ? 'Profil de prestataire disponible.' : 'Provider profile available.'),
                    language
                  )}
                </p>
                {loadingProviderProfile ? <p>{language === 'fr' ? 'Chargement du profil...' : 'Loading profile...'}</p> : null}
              </div>
            </div>

            <div className="booking-agent__modal-grid">
              <section className="booking-agent__modal-card">
                <h4>{language === 'fr' ? 'Langues' : 'Languages'}</h4>
                <p>{formatProviderList(selectedProviderProfile.languages).join(', ') || (language === 'fr' ? 'Non indiqué' : 'Not listed')}</p>
              </section>

              <section className="booking-agent__modal-card">
                <h4>{language === 'fr' ? 'Spécialités' : 'Specialties'}</h4>
                <p>{formatProviderList(selectedProviderProfile.specialities).join(', ') || (language === 'fr' ? 'Non indiqué' : 'Not listed')}</p>
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default BookingAgentPage;
